import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLine, Overline } from "@/components/Reveal";
import ClientMarquee from "@/components/ClientMarquee";
import { CASE_STUDIES, SERVICES } from "@/data/site";

const Hero = () => {
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  const onMouseMove = (e) => {
    const { innerWidth, innerHeight } = window;
    setTilt({
      x: (e.clientX / innerWidth - 0.5) * 2,
      y: (e.clientY / innerHeight - 0.5) * 2,
    });
  };

  return (
    <section
      className="relative min-h-screen flex items-center overflow-hidden grid-bg"
      onMouseMove={onMouseMove}
      data-testid="home-hero"
    >
      <motion.div
        className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[42rem] h-[42rem] rounded-full border border-cyan-400/15 animate-spin-slower"
        style={{ x: tilt.x * -18, y: tilt.y * -12 }}
        aria-hidden="true"
      >
        <span className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-cyan-400 rounded-full cyan-glow" />
      </motion.div>
      <motion.div
        className="absolute -top-40 -right-40 w-[34rem] h-[34rem] rounded-full bg-cyan-400/10 blur-[140px]"
        style={{ x: tilt.x * 30, y: tilt.y * 24 }}
        aria-hidden="true"
      />
      <motion.div
        className="absolute top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-cyan-400 to-transparent animate-beam"
        initial={{ left: "0%" }}
        animate={{ left: "100%" }}
        transition={{ duration: 2.2, ease: [0.7, 0, 0.2, 1] }}
        aria-hidden="true"
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pt-32 pb-20 w-full">
        <MaskedLine delay={0.15}>
          <Overline>Market Rounds Technologies — UK Creative Engineering Agency</Overline>
        </MaskedLine>
        <h1
          data-testid="hero-title-heading"
          className="font-display uppercase tracking-tighter leading-[1.02] text-5xl sm:text-6xl lg:text-8xl mt-8"
        >
          <MaskedLine delay={0.3}>We engineer</MaskedLine>
          <MaskedLine delay={0.45}>
            <span className="text-stroke">digital</span>
          </MaskedLine>
          <MaskedLine delay={0.6}>
            gravity<span className="text-cyan-400">.</span>
          </MaskedLine>
        </h1>
        <div className="mt-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10">
          <MaskedLine delay={0.85} className="max-w-md">
            <p className="text-zinc-400 text-base sm:text-lg leading-relaxed">
              Bespoke websites, mobile apps — and our own marketing platform that
              promotes AI products and services. Built in Nottingham, deployed
              worldwide, designed to pull your market toward you.
            </p>
          </MaskedLine>
          <motion.div
            className="flex flex-wrap gap-4"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <Link
              to="/case-studies"
              data-testid="hero-cta-explore-work"
              className="inline-flex items-center gap-2 bg-cyan-400 text-black font-display font-bold text-sm uppercase tracking-wider px-8 py-4 hover:bg-white transition-colors duration-300"
            >
              Explore our work
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-cta-start-project"
              className="inline-flex items-center gap-2 border border-zinc-700 text-white font-display font-bold text-sm uppercase tracking-wider px-8 py-4 hover:border-cyan-400 hover:text-cyan-400 transition-colors duration-300"
            >
              Start a project
            </Link>
          </motion.div>
        </div>
      </div>

      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        aria-hidden="true"
      >
        <div className="w-px h-16 bg-gradient-to-b from-transparent via-zinc-600 to-cyan-400" />
      </motion.div>
    </section>
  );
};

const MANIFESTO = [
  {
    n: "01",
    title: "Architect",
    body: "Every build starts as an engineering blueprint — content models, user flows and conversion paths mapped before a single pixel ships.",
  },
  {
    n: "02",
    title: "Design",
    body: "Kinetic, editorial, unmistakably yours. Interfaces with the confidence of a brand that knows exactly who it is.",
  },
  {
    n: "03",
    title: "Amplify",
    body: "Launch is the starting line. AI Warehouse, our own marketing platform, keeps promoting your product — and gives AI products and services the audience they deserve.",
  },
];

const Manifesto = () => (
  <section className="py-24 lg:py-36" data-testid="home-manifesto">
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
      <Reveal>
        <Overline>The Method</Overline>
        <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl mt-4 max-w-2xl leading-tight">
          Three disciplines. One gravitational pull.
        </h2>
      </Reveal>
      <div className="grid md:grid-cols-3 mt-16 border-t border-l border-zinc-800">
        {MANIFESTO.map((ch, i) => (
          <Reveal key={ch.n} delay={i * 0.12} className="h-full">
            <div className="group h-full border-b border-r border-zinc-800 p-8 lg:p-10 hover:bg-[#0A0A0C] transition-colors duration-500">
              <span className="font-mono-accent text-cyan-400 text-sm">{ch.n}/</span>
              <h3 className="font-display font-bold uppercase text-2xl mt-6 group-hover:text-cyan-400 transition-colors duration-300">
                {ch.title}
              </h3>
              <p className="text-zinc-400 text-sm leading-relaxed mt-4">{ch.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const FeaturedWork = () => (
  <section className="py-24 lg:py-36 bg-white text-black border-y border-zinc-200" data-testid="home-featured-work">
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
      <Reveal className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
        <div>
          <Overline className="text-cyan-600">Selected Work</Overline>
          <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl mt-4 leading-tight">
            Proof, not promises
          </h2>
        </div>
        <Link
          to="/case-studies"
          data-testid="home-all-case-studies-link"
          className="inline-flex items-center gap-2 font-mono-accent text-xs uppercase tracking-[0.25em] text-cyan-600 hover:text-black transition-colors"
        >
          All case studies <ArrowUpRight className="w-4 h-4" />
        </Link>
      </Reveal>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200 mt-16">
        {CASE_STUDIES.filter((cs) => cs.slug !== "jenim-foods").map((cs, i) => (
          <Reveal key={cs.slug} delay={i * 0.1}>
            <a
              href={cs.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={cs.testId}
              className="group block bg-white overflow-hidden h-full"
            >
              <div className="relative overflow-hidden aspect-[16/10]">
                <img
                  src={cs.imageFull}
                  alt={`${cs.client} website`}
                  className="w-full h-full object-cover case-pan is-active group-hover:scale-[1.04] transition-transform duration-700 ease-out"
                  style={{ animationDelay: `${i * 3.5}s` }}
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                <span className="absolute top-4 left-4 font-mono-accent text-[10px] uppercase tracking-[0.25em] bg-black/70 backdrop-blur px-3 py-2 text-cyan-400 border border-zinc-800">
                  {cs.category}
                </span>
              </div>
              <div className="p-6 lg:p-8 flex items-start justify-between gap-6">
                <div>
                  <h3 className="font-display font-bold uppercase text-xl lg:text-2xl group-hover:text-cyan-600 transition-colors">
                    {cs.client}
                  </h3>
                  <p className="text-zinc-600 text-sm mt-2">{cs.title}</p>
                </div>
                <span className="shrink-0 w-11 h-11 grid place-items-center border border-zinc-300 group-hover:border-cyan-500 group-hover:bg-cyan-400 group-hover:text-black transition-all duration-300">
                  <ArrowUpRight className="w-5 h-5" />
                </span>
              </div>
            </a>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const ServicesMatrix = () => (
  <section className="py-24 lg:py-36" data-testid="home-services">
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
      <Reveal>
        <Overline>What We Do</Overline>
        <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl mt-4 leading-tight">
          Built to be found.<br />Built to be felt.
        </h2>
      </Reveal>
      <div className="mt-16 space-y-px bg-zinc-800 border border-zinc-800">
        {SERVICES.map((s, i) => (
          <Reveal key={s.id} delay={i * 0.08}>
            <div
              data-testid={s.testId}
              className="group grid lg:grid-cols-12 gap-6 items-center bg-black p-8 lg:p-10 hover:bg-[#0A0A0C] transition-colors duration-500"
            >
              <span className="lg:col-span-1 font-mono-accent text-cyan-400 text-sm">{s.index}/</span>
              <h3 className="lg:col-span-4 font-display font-bold uppercase text-2xl group-hover:text-cyan-400 transition-colors">
                {s.title}
              </h3>
              <p className="lg:col-span-6 text-zinc-400 text-sm leading-relaxed">{s.hook} {s.description.split(".")[0]}.</p>
              <Link
                to="/services"
                className="lg:col-span-1 justify-self-end w-11 h-11 grid place-items-center border border-zinc-700 group-hover:border-cyan-400 group-hover:text-cyan-400 transition-all"
                aria-label={`More about ${s.title}`}
              >
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const CtaBanner = () => (
  <section className="relative py-28 lg:py-40 overflow-hidden bg-white text-black border-t border-zinc-200" data-testid="home-cta-banner">
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[36rem] h-[36rem] bg-cyan-400/20 blur-[160px] rounded-full" aria-hidden="true" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 text-center">
      <Reveal>
        <Overline className="text-cyan-600">Next Move Is Yours</Overline>
        <h2 className="font-display font-extrabold uppercase tracking-tighter leading-[0.95] text-4xl sm:text-6xl lg:text-7xl mt-6">
          Let's build something<br />
          <span className="italic text-cyan-500">commanding</span>
        </h2>
        <Link
          to="/contact"
          data-testid="home-cta-banner-button"
          className="inline-flex items-center gap-2 bg-black text-white font-display font-bold text-sm uppercase tracking-wider px-10 py-5 mt-12 hover:bg-cyan-400 hover:text-black transition-colors duration-300"
        >
          Start the conversation <ArrowUpRight className="w-4 h-4" />
        </Link>
      </Reveal>
    </div>
  </section>
);

const Home = () => (
  <main data-testid="home-page">
    <Hero />
    <ClientMarquee />
    <Manifesto />
    <FeaturedWork />
    <ServicesMatrix />
    <CtaBanner />
  </main>
);

export default Home;
